
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { TimeInput } from "@/components/ui/time-input";
import { WeeklyHours, DAYS_OF_WEEK, DAY_NAMES } from '@communitly/shared';
import { useTimeFormatParameters } from '@/hooks/useParameters';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

interface OpeningHoursFormProps {
  openingHours: WeeklyHours;
  onChange: (hours: WeeklyHours) => void;
}

const OpeningHoursForm: React.FC<OpeningHoursFormProps> = ({
  openingHours,
  onChange
}) => {
  const { timeDisplayFormat } = useTimeFormatParameters();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const updateDayHours = (day: string, field: string, value: string | boolean) => {
    const updatedHours = {
      ...openingHours,
      [day]: {
        ...openingHours[day as keyof WeeklyHours],
        [field]: value
      }
    };
    onChange(updatedHours);
  };

  const toggleDayClosed = (day: string, closed: boolean) => {
    const updatedHours = {
      ...openingHours,
      [day]: {
        open: openingHours[day as keyof WeeklyHours]?.open || '09:00',
        close: openingHours[day as keyof WeeklyHours]?.close || '17:00',
        closed: closed
      }
    };
    onChange(updatedHours);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('service.details.opening_hours')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {DAYS_OF_WEEK.map(day => {
          const dayHours = openingHours[day];
          const isClosed = dayHours?.closed ?? false;
          
          if (isMobile) {
            // Mobile layout: stack elements vertically for better touch experience
            return (
              <div key={day} className="space-y-3 p-4 rounded-lg border bg-card">
                {/* Day name */}
                <div>
                  <Label className="text-base font-medium">{t(DAY_NAMES[day])}</Label>
                </div>
                
                {/* Open/Closed toggle */}
                <div className="flex items-center space-x-3">
                  <Switch
                    checked={!isClosed}
                    onCheckedChange={(checked) => toggleDayClosed(day, !checked)}
                  />
                  <Label className="text-sm font-medium">
                    {isClosed ? t('service.details.day_names.closed') : 'Open'}
                  </Label>
                </div>
                
                {/* Time inputs */}
                {!isClosed && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-3 items-center">
                      <TimeInput
                        value={dayHours?.open || '09:00'}
                        onChange={(value) => updateDayHours(day, 'open', value)}
                        timeFormat={timeDisplayFormat}
                        className="h-12 text-base"
                      />
                      <TimeInput
                        value={dayHours?.close || '17:00'}
                        onChange={(value) => updateDayHours(day, 'close', value)}
                        timeFormat={timeDisplayFormat}
                        className="h-12 text-base"
                      />
                    </div>
                    <div className="text-center text-sm text-muted-foreground">
                      Start time ↔ End time
                    </div>
                  </div>
                )}
              </div>
            );
          } else {
            // Desktop layout: keep current horizontal grid layout
            return (
              <div key={day} className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-3">
                  <Label className="font-medium">{t(DAY_NAMES[day])}</Label>
                </div>
                
                <div className="col-span-2 flex items-center space-x-2">
                  <Switch
                    checked={!isClosed}
                    onCheckedChange={(checked) => toggleDayClosed(day, !checked)}
                  />
                  <Label className="text-sm">Open</Label>
                </div>
                
                {!isClosed && (
                  <>
                    <div className="col-span-3">
                      <TimeInput
                        value={dayHours?.open || '09:00'}
                        onChange={(value) => updateDayHours(day, 'open', value)}
                        timeFormat={timeDisplayFormat}
                      />
                    </div>
                    
                    <div className="col-span-1 text-center text-sm text-muted-foreground">
                      to
                    </div>
                    
                    <div className="col-span-3">
                      <TimeInput
                        value={dayHours?.close || '17:00'}
                        onChange={(value) => updateDayHours(day, 'close', value)}
                        timeFormat={timeDisplayFormat}
                      />
                    </div>
                  </>
                )}
                
                {isClosed && (
                  <div className="col-span-7 text-sm text-muted-foreground">
                    {t('service.details.day_names.closed')}
                  </div>
                )}
              </div>
            );
          }
        })}
      </CardContent>
    </Card>
  );
};

export default OpeningHoursForm;
